import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { SsbRaceResult, SsbReferenceGroupResult } from '../../types/SimilarlySituated/SsbAnalysisResult.type';
import { SsbRateMode } from '../../types/enums/SsbRateMode.enum';
import {
  formatCount,
  formatExcess,
  formatRate,
  formatRelativeRisk,
  notEstimableReason,
  raceLabel,
  resultRaces,
  totalResult,
} from '../../utils/ssbResults';

interface OutcomeResultTableProps {
  group: SsbReferenceGroupResult;
  mode: SsbRateMode;
}

const CELL_SX = { fontSize: '14px', borderColor: '#E2E8F0', paddingY: '12px' };
const HEAD_SX = { fontSize: '12px', fontWeight: 500, color: '#718096', borderColor: '#E2E8F0', paddingY: '6px' };
const MUTED = '#718096';

function OutcomeResultTable({ group, mode }: OutcomeResultTableProps) {
  const isPerCapita = mode === SsbRateMode.PER_CAPITA;
  const races = resultRaces(group);

  const renderValues = (row: SsbRaceResult, isTotal = false) => {
    const reason = isTotal ? null : notEstimableReason(row, mode);

    return (
      <>
        <TableCell sx={{ ...CELL_SX, fontWeight: isTotal ? 600 : 400 }}>{row.in_box}</TableCell>
        <TableCell sx={{ ...CELL_SX, color: MUTED }}>{row.indeterminate}</TableCell>
        {reason ? (
          <TableCell colSpan={5} sx={{ ...CELL_SX, color: MUTED, fontStyle: 'italic' }}>
            not estimable — {reason}
          </TableCell>
        ) : (
          <>
            <TableCell sx={{ ...CELL_SX, fontWeight: isTotal ? 600 : 400 }}>{formatCount(row.denominator)}</TableCell>
            <TableCell sx={{ ...CELL_SX, fontWeight: isTotal ? 600 : 400 }}>{formatCount(row.with_outcome)}</TableCell>
            <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{formatRate(row.rate, mode)}</TableCell>
            <TableCell sx={CELL_SX}>{formatRelativeRisk(row.relative_risk)}</TableCell>
            <TableCell sx={CELL_SX}>{formatExcess(row.excess)}</TableCell>
          </>
        )}
      </>
    );
  };

  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell sx={HEAD_SX}>Race</TableCell>
          <TableCell sx={HEAD_SX}>In box</TableCell>
          <TableCell sx={HEAD_SX}>Indet.</TableCell>
          <TableCell sx={HEAD_SX}>{isPerCapita ? 'Population' : 'Denominator'}</TableCell>
          <TableCell sx={HEAD_SX}>w/ outcome</TableCell>
          <TableCell sx={HEAD_SX}>Rate</TableCell>
          <TableCell sx={HEAD_SX}>Rel. risk</TableCell>
          <TableCell sx={HEAD_SX}>Excess</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {races.map((race) => {
          const row = group.results_by_race[race] as SsbRaceResult;
          const isNotEstimable = notEstimableReason(row, mode) !== null;

          return (
            <TableRow key={race} sx={{ backgroundColor: isNotEstimable ? '#F7FAFC' : undefined }}>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: isNotEstimable ? MUTED : undefined }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '8px' }}>
                  {raceLabel(race)}
                  {race === group.racial_reference_group && (
                    <Typography component='span' sx={{ fontSize: '12px', fontWeight: 600, color: '#1D4ED8' }}>
                      (ref)
                    </Typography>
                  )}
                </Box>
              </TableCell>
              {renderValues(row)}
            </TableRow>
          );
        })}
        <TableRow sx={{ '& td': { borderTop: '3px solid #A0AEC0', borderBottom: 'none' } }}>
          <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>Total</TableCell>
          {renderValues(totalResult(group, mode), true)}
        </TableRow>
      </TableBody>
    </Table>
  );
}

export default OutcomeResultTable;
